import { useCallback } from 'react';
import { useAppContext } from '@/contexts/AppContext';
import { deDE } from '@/locales/de';

interface TranslationOptions {
  returnObjects?: boolean;
}

// Interface für mehrsprachige Texte
interface LocalizedString {
  de: string;
  en?: string;
  // Weitere Sprachen hier hinzufügen
}

// Rekursiver Typ für einen Block von Übersetzungen
interface TranslationBlockObject {
  [key: string]: LocalizedString | TranslationBlockObject;
}

// Exportierter Typ für die API
export type TranslationBlock = TranslationBlockObject;

// Globale Variable zum Speichern von Seitenübersetzungen
const pageTranslations: Record<string, TranslationBlock> = {};

// Funktion zum Registrieren von seitenspezifischen Übersetzungen
export const registerTranslations = (pageName: string, translations: TranslationBlock) => {
  pageTranslations[pageName] = translations;
};

// Funktion zum Definieren von Übersetzungen
export const defineMessages = (messages: TranslationBlock): TranslationBlock => {
  return messages;
};

// Eine einfache Methode, um auf Übersetzungen zuzugreifen
// Beispielaufruf: t('navigation.home')
export const useTranslation = () => {
  const { language } = useAppContext();
  
  // Unterstützte Sprachen
  const translations = {
    de: deDE,
    // Hier können weitere Sprachen hinzugefügt werden
    // en: enUS,
  };
  
  // Aktuelle Sprache auswählen
  const currentTranslations = translations[language] || deDE;
  
  // Funktion, um einen Übersetzungsschlüssel zu verarbeiten
  function t<T = string>(key: string, options?: TranslationOptions): T;
  function t(key: string, params?: Record<string, string>): string;
  function t<T = string>(key: string, optionsOrParams?: TranslationOptions | Record<string, string>): T | string {
    // Prüfen, ob es sich um einen seitenspezifischen Schlüssel handelt (z.B. 'aboutPage.hero.title')
    const keys = key.split('.');
    const pageName = keys[0];
    
    // Wenn die erste Ebene des Schlüssels eine registrierte Seite ist, verwende die Seitenübersetzungen
    if (pageTranslations[pageName]) {
      const pageKeys = keys.slice(1);
      let result = pageKeys.reduce((obj, k) => {
        return obj?.[k];
      }, pageTranslations[pageName] as any);
      
      // Wenn es sich um ein lokalisiertes Stringobjekt handelt, verwende die aktuelle Sprache
      if (result && typeof result === 'object' && result[language]) {
        result = result[language];
      } else if (result && typeof result === 'object' && result.de) {
        // Fallback auf Deutsch, wenn die aktuelle Sprache nicht verfügbar ist
        result = result.de;
      }
      
      // Für die returnObjects Option
      if (optionsOrParams && 'returnObjects' in optionsOrParams && optionsOrParams.returnObjects) {
        return result as T;
      }
      
      // Wenn keine Übersetzung gefunden wurde, greife auf die globalen Übersetzungen zurück
      if (typeof result !== 'string') {
        // Versuche es in den globalen Übersetzungen
        result = keys.reduce((obj, k) => {
          return obj?.[k];
        }, currentTranslations as any);
      }
      
      // Parameter ersetzen, falls vorhanden
      if (typeof result === 'string' && optionsOrParams && !('returnObjects' in optionsOrParams)) {
        Object.keys(optionsOrParams).forEach(paramKey => {
          result = result.replace(new RegExp(`{{${paramKey}}}`, 'g'), optionsOrParams[paramKey]);
        });
      }
      
      return result || key;
    }
    
    // Standard-Verhalten für globale Übersetzungen
    let result = keys.reduce((obj, k) => {
      return obj?.[k];
    }, currentTranslations as any);
    
    // Für die returnObjects Option
    if (optionsOrParams && 'returnObjects' in optionsOrParams && optionsOrParams.returnObjects) {
      return result as T;
    }
    
    // Wenn keine Übersetzung gefunden wurde, gib den Schlüssel zurück
    if (typeof result !== 'string') {
      console.warn(`Übersetzung nicht gefunden für: ${key}`);
      return key;
    }
    
    // Parameter ersetzen, falls vorhanden
    if (optionsOrParams && !('returnObjects' in optionsOrParams)) {
      Object.keys(optionsOrParams).forEach(paramKey => {
        result = result.replace(new RegExp(`{{${paramKey}}}`, 'g'), optionsOrParams[paramKey]);
      });
    }
    
    return result;
  }
  
  return { t, language };
};